import React, { useEffect, useRef, useState } from 'react';
import { Badge, Dialog, Tooltip } from '@mui/material';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { NotificationDto, notificationsApi } from 'services/camMirrorService';
import { getCamBaseUrl } from 'utils/camAuth';
import { formatModalDate, IconBell, IconClose } from './gmModal';

const TODO_TYPES = new Set(['todo_assigned', 'task_completed']);

const NotificationsBell: React.FC<{ onOpenTodos?: () => void; onUnreadChange?: (count: number) => void }> = ({
  onOpenTodos,
  onUnreadChange,
}) => {
  const history = useHistory();
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [open, setOpen] = useState(false);
  const extraPages = useRef(false);

  const load = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    notificationsApi
      .list()
      .then((result) => {
        setItems((prev) => {
          if (!extraPages.current) return result.items;
          const freshIds = new Set(result.items.map((row) => row.id));
          return [...result.items, ...prev.filter((row) => !freshIds.has(row.id))];
        });
        if (!extraPages.current) setNextCursor(result.nextCursor);
      })
      .catch(() => undefined);
  };

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    notificationsApi
      .list(nextCursor)
      .then((result) => {
        extraPages.current = true;
        setItems((prev) => {
          const seen = new Set(prev.map((row) => row.id));
          return [...prev, ...result.items.filter((row) => !seen.has(row.id))];
        });
        setNextCursor(result.nextCursor);
      })
      .catch(() => undefined)
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 60000);
    document.addEventListener('visibilitychange', load);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', load);
    };
  }, []);

  const unread = items.filter((item) => item.targetApp === 'gm' && !item.gmReadAt).length;
  useEffect(() => {
    onUnreadChange?.(unread);
  }, [unread, onUnreadChange]);

  const openItem = async (item: NotificationDto) => {
    setOpen(false);
    if (item.targetApp !== 'gm') return;
    if (!item.gmReadAt) {
      setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, gmReadAt: new Date().toISOString() } : row)));
      try {
        await notificationsApi.markRead(item.id);
      } catch {
        load();
      }
    }
    try {
      const resolved = await notificationsApi.resolve(item.id);
      if (resolved.status === 'other_app') {
        toast('Open CAM to view this notification.');
        return;
      }
      if (resolved.status === 'target_deleted') {
        toast.error('That item is no longer available.');
        return;
      }
      const link = resolved.deepLink || item.deepLink;
      if (!link) return;
      if (link.startsWith('/')) history.push(link);
      else if (link.startsWith('#')) history.push(link.slice(1));
    } catch {
      load();
    }
  };

  const markAll = async () => {
    setItems((prev) => prev.map((row) => (row.targetApp === 'gm' ? { ...row, gmReadAt: row.gmReadAt || new Date().toISOString() } : row)));
    try {
      await notificationsApi.markAllRead();
    } catch {
      load();
    }
  };

  return (
    <>
      <Tooltip title="Notifications" placement="bottom" enterDelay={200}>
        <button type="button" className="gm-bar-icon" aria-label="Notifications" onClick={() => setOpen(true)}>
          <Badge
            badgeContent={unread}
            invisible={unread === 0}
            sx={{ '& .MuiBadge-badge': { background: '#3E73F0', color: '#fff', fontWeight: 700, border: '2px solid #F7FBFD' } }}
          >
            <IconBell />
          </Badge>
        </button>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ className: 'gm-modal' }}>
        <div className="gm-modal-head">
          <div className="gm-modal-head-icon">
            <IconBell />
          </div>
          <div className="gm-modal-head-text">
            <div className="gm-modal-title">
              Notifications
              <span className="gm-modal-title-count">({items.length})</span>
            </div>
            <div className="gm-modal-subtitle">
              {unread > 0 ? `${unread} unread` : "You're all caught up."}
            </div>
          </div>
          <button type="button" className="gm-modal-close" aria-label="Close notifications" onClick={() => setOpen(false)}>
            <IconClose />
          </button>
        </div>
        <div className="gm-modal-body">
          <div className="gm-list-label" style={{ marginTop: 0 }}>
            <span className="gm-list-label-title">Recent</span>
            {unread > 0 ? (
              <button type="button" className="gm-text-btn" onClick={markAll}>
                Mark all read
              </button>
            ) : (
              <span className="gm-list-label-count">{items.length === 1 ? '1 notification' : `${items.length} notifications`}</span>
            )}
          </div>
          {items.length === 0 ? (
            <div className="gm-modal-empty">You&apos;re all caught up. Nothing new to review.</div>
          ) : (
            items.map((item) => {
              const body = (
                <div className="gm-item-body">
                  <div className="gm-item-title">
                    <span>{item.title || item.type}</span>
                    {item.targetApp !== 'gm' && !TODO_TYPES.has(item.type) ? <span className="gm-cam-link">(CAM)</span> : null}
                  </div>
                  {item.message ? <div className="gm-item-meta">{item.message}</div> : null}
                  <div className="gm-item-meta">{formatWhen(item.createdAt)}</div>
                </div>
              );
              if (TODO_TYPES.has(item.type) && onOpenTodos) {
                return (
                  <button key={item.id} type="button" className="gm-item-card" onClick={() => { setOpen(false); onOpenTodos(); }}>
                    {body}
                  </button>
                );
              }
              if (item.targetApp !== 'gm') {
                return (
                  <a
                    key={item.id}
                    className="gm-item-card"
                    href={camNotificationHref(item.deepLink)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {body}
                  </a>
                );
              }
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`gm-item-card${!item.gmReadAt ? ' unread' : ''}`}
                  onClick={() => openItem(item)}
                >
                  {body}
                </button>
              );
            })
          )}
          {nextCursor ? (
            <button type="button" className="gm-text-btn" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          ) : null}
        </div>
      </Dialog>
    </>
  );
};

function camNotificationHref(deepLink: string) {
  if (/^https?:\/\//i.test(deepLink)) return deepLink;
  const path = deepLink.startsWith('/') ? deepLink : `/${deepLink}`;
  return `${getCamBaseUrl()}${path}`;
}

function formatWhen(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return formatModalDate(value);
}

export default NotificationsBell;
